import { capitalize } from 'lodash';

import Summary from 'models/aiArtifacts/Summary';
import AIJob, { AIJobOptions } from 'models/AIJob';
import Classification, { ClassificationOptions } from 'models/Classification';
import Episode, { EpisodeOptions } from 'models/Episode';
import Group, { GroupOptions } from 'models/Group';
import LocationEpisode, { LocationEpisodeOptions } from 'models/LocationEpisode';
import Patient, { PatientOptions } from 'models/Patient';
import Review, {
  BaseReviewDataOptions,
  getDefaults as getReviewDefaults,
  ReviewOptions,
  ReviewType,
} from 'models/Review';

export type ReferralData = Partial<
  {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    phoneNumber?: string;
    expectedDischargeDate?: string;
  } & BaseReviewDataOptions
>;

export interface ReferralOptions extends Omit<ReviewOptions, 'data'> {
  type: ReviewType.REFERRAL;
  denialReasonOptions: { apiName: string; displayName: string }[];
  episode: Partial<EpisodeOptions>;
  locationEpisode: Partial<LocationEpisodeOptions>;
  planTypeClassification?: Partial<ClassificationOptions>;
  patient?: Partial<PatientOptions>;
  referredBy?: Partial<GroupOptions>;
  hasNewChanges?: boolean;
  summaryJobForCurrentAttachments?: AIJobOptions | null;
  data: ReferralData;
}

export function getDefaults(): ReferralOptions {
  const defaults = getReviewDefaults();
  return {
    ...defaults,
    type: ReviewType.REFERRAL,
    denialReasonOptions: [],
    episode: {} as EpisodeOptions,
    locationEpisode: {} as LocationEpisodeOptions,
    planTypeClassification: {} as ClassificationOptions,
    patient: {} as PatientOptions,
    referredBy: {} as GroupOptions,
    hasNewChanges: false,
    summaryJobForCurrentAttachments: null,
    data: {} as ReferralData,
  };
}

export default class Referral extends Review {
  declare type: ReviewType.REFERRAL;
  declare groupId: string;
  denialReasonOptions: { apiName: string; displayName: string }[];
  episode: Episode;
  locationEpisode: LocationEpisode;
  planTypeClassification?: Classification;
  patient?: Patient;
  referredBy?: Group;
  hasNewChanges: boolean;
  summaryJobForCurrentAttachments: AIJob | null;
  data: ReferralData;
  constructor(options: Partial<ReferralOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    super(opts);

    this.denialReasonOptions = options.denialReasonOptions || [];
    this.episode = new Episode(options.episode);
    this.locationEpisode = new LocationEpisode(options.locationEpisode);
    this.planTypeClassification = options.planTypeClassification
      ? new Classification(options.planTypeClassification)
      : options.planTypeClassification;
    this.patient = options.patient ? new Patient(options.patient) : options.patient;
    this.referredBy = options.referredBy ? new Group(options.referredBy) : options.referredBy;
    this.hasNewChanges = options.hasNewChanges || false;
    this.summaryJobForCurrentAttachments = opts.summaryJobForCurrentAttachments
      ? new AIJob(opts.summaryJobForCurrentAttachments)
      : null;
    this.data = options.data || ({} as ReferralData);
  }

  static get type() {
    return ReviewType.REFERRAL;
  }

  get committed() {
    return this.pending || this.accepted;
  }

  get statusLabel() {
    return this.rejected ? 'Denied' : capitalize(this.status);
  }

  get summaryJob(): AIJob {
    return this.summaryJobForCurrentAttachments as AIJob;
  }

  get summary(): Summary | undefined {
    return this.summaryJob?.aiArtifact as Summary | undefined;
  }
}
